/**
 * Workbook Brain Overview Component
 * Displays comprehensive workbook analysis: data quality, risks, performance, optimization
 */

import React, { useState } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorDisplay } from '../common/ErrorDisplay';
import type {
  WorkbookScanResult,
  WorkbookUnderstanding,
  AnalyzeWorkbookResponse,
  OptimizationPriority,
  WorkbookRiskReport,
  WorkbookPerformanceReport,
  WorkbookOptimizationPlan,
  SheetScanResult,
} from '@excel-ai-studio/shared';
import '../common/PreviewPanel.css';
import { createAnalysisSummarySheet, createRecommendationsSheet } from '../../services/workbook-brain-writer.service';
import { callAnalyzeAPI } from '../../services/workbook-brain-api.service';
import { implementOptimization, rollbackOptimizationChanges, hasUndoableChanges } from '../../services/optimization-executor.service';

/* global Excel */

interface WorkbookBrainOverviewProps {
  onAnalysisComplete?: (response: AnalyzeWorkbookResponse) => void;
}

// --- Styles ---
const cardStyle: React.CSSProperties = {
  padding: '12px',
  backgroundColor: '#f5f5f5',
  borderRadius: '4px',
  marginBottom: '12px',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: '14px',
};

const smallTextStyle: React.CSSProperties = {
  margin: '0', fontSize: '12px', color: '#666', lineHeight: 1.5,
};

const badgeStyle = (color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '10px',
  fontSize: '11px',
  fontWeight: 'bold',
  color: '#fff',
  backgroundColor: color,
  marginRight: '4px',
});

function qualityColor(score: number): string {
  if (score >= 75) return '#4caf50';
  if (score >= 50) return '#ff9800';
  return '#f44336';
}

function severityColor(severity: string): string {
  switch (severity) {
    case 'critical': return '#d32f2f';
    case 'high': return '#f44336';
    case 'medium': return '#ff9800';
    default: return '#2196F3';
  }
}

function ratingLabel(rating: string): string {
  switch (rating) {
    case 'excellent': return 'Excellent';
    case 'good': return 'Bon';
    case 'fair': return 'Passable';
    case 'poor': return 'Faible';
    default: return rating;
  }
}

// --- Quality bar component ---
const QualityBar: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div style={{ marginBottom: 6 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#555' }}>
      <span>{label}</span>
      <span style={{ fontWeight: 'bold', color: qualityColor(value) }}>{value.toFixed(0)}%</span>
    </div>
    <div style={{ height: 6, backgroundColor: '#e0e0e0', borderRadius: 3 }}>
      <div style={{ height: 6, width: `${Math.min(100, value)}%`, backgroundColor: qualityColor(value), borderRadius: 3 }} />
    </div>
  </div>
);

// --- Sheet detail row ---
const SheetDetailRow: React.FC<{ sheet: SheetScanResult }> = ({ sheet }) => {
  const [expanded, setExpanded] = useState(false);
  const adv = sheet.advancedAnalysis;
  const dp = sheet.dataPatterns;

  const colTypes: string[] = [];
  if (dp?.numericColumns) colTypes.push(`${dp.numericColumns} num.`);
  if (dp?.textColumns) colTypes.push(`${dp.textColumns} texte`);
  if (dp?.dateColumns) colTypes.push(`${dp.dateColumns} date`);
  if (dp?.emptyColumns) colTypes.push(`${dp.emptyColumns} vides`);

  return (
    <div style={{ marginBottom: 6, padding: '8px', backgroundColor: 'white', borderRadius: 4, borderLeft: '4px solid #2196F3' }}>
      <div
        onClick={() => adv && setExpanded(!expanded)}
        style={{ cursor: adv ? 'pointer' : 'default', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div>
          <p style={{ margin: '0 0 2px', fontSize: 12, fontWeight: 'bold' }}>{sheet.name}</p>
          <p style={{ margin: 0, fontSize: 11, color: '#666' }}>
            {sheet.rowCount} lignes, {sheet.columnCount} col. | {colTypes.join(', ') || 'N/A'} | {sheet.formulaCount} formules
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          {adv && (
            <span style={badgeStyle(qualityColor(adv.overallQuality))}>
              {adv.overallQuality.toFixed(0)}%
            </span>
          )}
          {!adv && dp && (
            <span style={badgeStyle(dp.estimatedDataQuality === 'high' ? '#4caf50' : dp.estimatedDataQuality === 'medium' ? '#ff9800' : '#f44336')}>
              {dp.estimatedDataQuality}
            </span>
          )}
          {adv && <span style={{ fontSize: 10, color: '#999' }}>{expanded ? ' ▲' : ' ▼'}</span>}
        </div>
      </div>

      {expanded && adv && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
          <QualityBar label="Complétude" value={adv.completeness} />
          <QualityBar label="Cohérence" value={adv.consistency} />
          <QualityBar label="Unicité" value={adv.uniqueness} />
          <QualityBar label="Validité" value={adv.validity} />

          {adv.patterns.hasDuplicateRows > 0 && (
            <p style={{ ...smallTextStyle, color: '#e65100' }}>
              {adv.patterns.hasDuplicateRows} ligne(s) dupliquée(s)
            </p>
          )}

          {adv.recommendations.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 'bold', color: '#555' }}>Recommandations:</p>
              {adv.recommendations.map((rec, i) => (
                <p key={i} style={{ margin: '2px 0', fontSize: 11, color: '#666' }}>{rec}</p>
              ))}
            </div>
          )}

          {/* Column details */}
          {adv.columns.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 'bold', color: '#555' }}>
                Colonnes ({adv.columns.length}):
              </p>
              <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                {adv.columns.map((col, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '2px 0', borderBottom: '1px solid #f5f5f5' }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.name}</span>
                    <span style={{ width: 50, textAlign: 'center', color: '#999' }}>{col.type}</span>
                    <span style={{ width: 40, textAlign: 'right' }}>{col.fillRate.toFixed(0)}%</span>
                    <span style={{ ...badgeStyle(qualityColor(col.quality === 'excellent' ? 90 : col.quality === 'good' ? 70 : col.quality === 'fair' ? 40 : 10)), marginLeft: 4, fontSize: 9 }}>
                      {col.quality}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Workbook Brain Overview Component
 */
export const WorkbookBrainOverview: React.FC<WorkbookBrainOverviewProps> = ({
  onAnalysisComplete,
}) => {
  const [scanResult, setScanResult] = useState<WorkbookScanResult | null>(null);
  const [understanding, setUnderstanding] = useState<WorkbookUnderstanding | null>(null);
  const [riskReport, setRiskReport] = useState<WorkbookRiskReport | null>(null);
  const [performanceReport, setPerformanceReport] = useState<WorkbookPerformanceReport | null>(null);
  const [optimizationPlan, setOptimizationPlan] = useState<WorkbookOptimizationPlan | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isApplyingFix, setIsApplyingFix] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [appliedFixes, setAppliedFixes] = useState<Set<string>>(new Set());

  const mapCriticalAreaToPriority = (
    area: string,
  ): { priority: OptimizationPriority; label: string } | null => {
    const a = area.toLowerCase();

    const extractSheetName = (s: string): string | null => {
      const cleaned = s.trim().replace(/^[^a-zA-Z\u00C0-\u024F]+/, '');
      const m1 = /^(.+?)\s+a\s+/i.exec(cleaned);
      if (m1?.[1]) return m1[1].trim();
      const m2 = /^(.+?)\s+contient\s+/i.exec(cleaned);
      if (m2?.[1]) return m2[1].trim();
      const m3 = /^(.+?)\s+:/i.exec(cleaned);
      if (m3?.[1]) return m3[1].trim();
      return null;
    };

    const targetSheet = extractSheetName(area);

    // Empty columns remediation
    if (a.includes('colonnes') && a.includes('vides')) {
      return {
        label: 'Supprimer colonnes vides',
        priority: {
          title: 'Supprimer les colonnes vides',
          impact: 'high',
          effort: 'medium',
          description: 'Suppression des colonnes vides identifiées dans le classeur.',
          suggestedActions: ['Identifier les colonnes vides', 'Supprimer / réduire les colonnes inutiles'],
          affectedAreas: targetSheet ? [targetSheet] : undefined,
          actionKey: 'remove_empty_columns',
        },
      };
    }

    // Data quality cleanup
    if (a.includes('qualité') && (a.includes('données') || a.includes('globale')) && a.includes('faible')) {
      return {
        label: 'Identifier les données manquantes',
        priority: {
          title: 'Identifier les données manquantes',
          impact: 'high',
          effort: 'low',
          description: 'Identifie les cellules vides (aucune valeur modifiée).',
          suggestedActions: ['Identifier les vides', 'Vérifier manuellement'],
          affectedAreas: targetSheet ? [targetSheet] : undefined,
          actionKey: 'clean_missing_data',
        },
      };
    }

    // High formula density remediation (volatile functions)
    if (a.includes('densité') && a.includes('formules')) {
      return {
        label: 'Optimiser formules',
        priority: {
          title: 'Résoudre les problèmes de haute gravité',
          impact: 'high',
          effort: 'medium',
          description: 'Réduction de la volatilité et simplification ciblée des calculs.',
          suggestedActions: ['Remplacer les fonctions volatiles', 'Décomposer les calculs'],
          affectedAreas: targetSheet ? [targetSheet] : undefined,
          actionKey: 'replace_volatile_functions',
        },
      };
    }

    // Full column references (performance)
    if (a.includes('colonnes complètes') || a.includes('colonne complète') || a.includes('full column')) {
      return {
        label: 'Limiter colonnes complètes',
        priority: {
          title: 'Limiter les formules de colonne complète',
          impact: 'high',
          effort: 'medium',
          description: 'Réduction des références de colonnes complètes (améliore les performances).',
          suggestedActions: ['Limiter les références de colonne complète', 'Vérifier les dépendances et recalculer'],
          affectedAreas: targetSheet ? [targetSheet] : undefined,
          actionKey: 'limit_full_column_formulas',
        },
      };
    }

    // Missing values in formula sheets
    if (a.includes('valeurs manquantes') || (a.includes('manquante') && a.includes('données'))) {
      return {
        label: 'Identifier manquants',
        priority: {
          title: 'Identifier les données manquantes',
          impact: 'high',
          effort: 'low',
          description: 'Identifie les cellules vides (aucune valeur modifiée).',
          suggestedActions: ['Identifier les vides', 'Vérifier manuellement'],
          affectedAreas: targetSheet ? [targetSheet] : undefined,
          actionKey: 'clean_missing_data',
        },
      };
    }

    // Generic data cleaning
    if (a.includes('nettoyage')) {
      return {
        label: 'Nettoyer les données',
        priority: {
          title: 'Améliorer la qualité des données',
          impact: 'medium',
          effort: 'low',
          description: 'Exécuter un nettoyage des données pour réduire les incohérences.',
          suggestedActions: ['Nettoyer / normaliser les données'],
          affectedAreas: targetSheet ? [targetSheet] : undefined,
          actionKey: 'improve_data_quality',
        },
      };
    }

    return null;
  };

  const applyFixForCriticalArea = async (area: string) => {
    const mapped = mapCriticalAreaToPriority(area);
    if (!mapped) return;
    if (isApplyingFix) return;

    setIsApplyingFix(true);
    setMessage(null);
    try {
      const result = await implementOptimization(mapped.priority);
      if (result.success) {
        setCanUndo(hasUndoableChanges());
        setAppliedFixes((prev) => new Set([...prev, area]));
        setMessage({
          type: 'success',
          text: `Correction appliquée: ${mapped.label}${result.affectedCells !== undefined ? ` (${result.affectedCells} cellules affectées)` : ''}`,
        });
      } else {
        setMessage({ type: 'error', text: `Correction non appliquée: ${result.message}` });
      }
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : 'Erreur inconnue';
      setMessage({ type: 'error', text: `Échec: ${errMsg}` });
    } finally {
      setIsApplyingFix(false);
    }
  };

  const handleUndo = async () => {
    if (isUndoing) return;
    setIsUndoing(true);
    setMessage(null);
    try {
      const result = await rollbackOptimizationChanges();
      setCanUndo(hasUndoableChanges());
      if (result.success) {
        // Remove the most recent applied fix so it reappears in the list
        setAppliedFixes((prev) => {
          const arr = [...prev];
          arr.pop();
          return new Set(arr);
        });
        setMessage({ type: 'success', text: result.message });
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : 'Erreur inconnue';
      setMessage({ type: 'error', text: `Échec de l'annulation: ${errMsg}` });
    } finally {
      setIsUndoing(false);
    }
  };

  /**
   * Analyze workbook — enable ALL modules for comprehensive results
   */
  const analyzeWorkbook = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await callAnalyzeAPI({
        includeScanning: true,
        includeUnderstanding: true,
        includeDependencies: true,
        includeRiskAnalysis: true,
        includePerformanceAnalysis: true,
        includeDocumentation: false,
        includeOptimization: true,
      });

      if (result.scanResult) setScanResult(result.scanResult);
      if (result.understanding) setUnderstanding(result.understanding);
      if (result.riskReport) setRiskReport(result.riskReport);
      if (result.performanceReport) setPerformanceReport(result.performanceReport);
      if (result.optimizationPlan) setOptimizationPlan(result.optimizationPlan);

      if (onAnalysisComplete) {
        onAnalysisComplete(result);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSummarySheet = async () => {
    if (!understanding || !scanResult) return;
    
    try {
      setMessage(null);
      await createAnalysisSummarySheet({
        title: 'Résumé d\'Analyse du Classeur',
        sections: [
          {
            heading: 'Vue d\'ensemble',
            content: [understanding.summary],
          },
          {
            heading: 'Domaine métier',
            content: [understanding.domain],
          },
          {
            heading: 'Statistiques',
            content: [
              `Feuilles: ${scanResult.totals.sheetCount}`,
              `Formules: ${scanResult.totals.formulaCount}`,
              `Tableaux: ${scanResult.totals.tableCount}`,
              `Lignes totales: ${scanResult.totals.totalRows}`,
              `Complexité: ${understanding.estimatedComplexity}`,
            ],
          },
          ...(riskReport ? [{
            heading: 'Risques',
            content: [
              `Score de risque: ${riskReport.riskScore}/100`,
              `Total: ${riskReport.totalRisks} (${riskReport.criticalCount} critiques, ${riskReport.highCount} hauts)`,
              riskReport.summary,
            ],
          }] : []),
          ...(performanceReport ? [{
            heading: 'Performance',
            content: [
              `Score: ${performanceReport.score}/100 (${performanceReport.overallRating})`,
              ...performanceReport.quickWins.slice(0, 5),
            ],
          }] : []),
        ],
      });
      setMessage({ type: 'success', text: 'Feuille de résumé créée avec succès' });
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erreur lors de la création de la feuille';
      setMessage({ type: 'error', text: errorMsg });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleCreateRecommendationsSheet = async () => {
    if (!understanding) return;
    
    try {
      setMessage(null);
      const recommendations = understanding.criticalAreas.map(area => ({
        title: 'Zone critique',
        description: area,
        priority: 'high',
        effort: 'medium',
      }));
      await createRecommendationsSheet(recommendations);
      setMessage({ type: 'success', text: 'Feuille de recommandations créée avec succès' });
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erreur lors de la création de la feuille';
      setMessage({ type: 'error', text: errorMsg });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  // Aggregate quality scores from all sheets' advancedAnalysis
  const aggregateQuality = () => {
    if (!scanResult) return null;
    let totalQuality = 0, totalCompleteness = 0, totalConsistency = 0,
      totalUniqueness = 0, totalValidity = 0, count = 0;
    for (const sheet of scanResult.sheets) {
      if (sheet.advancedAnalysis) {
        count++;
        totalQuality += sheet.advancedAnalysis.overallQuality;
        totalCompleteness += sheet.advancedAnalysis.completeness;
        totalConsistency += sheet.advancedAnalysis.consistency;
        totalUniqueness += sheet.advancedAnalysis.uniqueness;
        totalValidity += sheet.advancedAnalysis.validity;
      }
    }
    if (count === 0) return null;
    return {
      quality: totalQuality / count,
      completeness: totalCompleteness / count,
      consistency: totalConsistency / count,
      uniqueness: totalUniqueness / count,
      validity: totalValidity / count,
    };
  };

  const qualityAgg = scanResult ? aggregateQuality() : null;

  return (
    <div className="preview-panel">
      <div className="preview-header">
        <h3>Cerveau Classeur</h3>
        <p className="preview-subtitle">Analyse approfondie de la structure, qualité et performance</p>
      </div>

      {error && <ErrorDisplay message={error} onDismiss={() => setError(null)} />}

      {message && (
        <div style={{ padding: '10px', backgroundColor: message.type === 'success' ? '#e8f5e9' : '#ffebee', color: message.type === 'success' ? '#2e7d32' : '#c62828', borderRadius: '4px', fontSize: '12px', marginBottom: 8 }}>
          {message.text}
        </div>
      )}

      {canUndo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', backgroundColor: '#e3f2fd', borderRadius: '4px', marginBottom: 8, borderLeft: '4px solid #1976D2' }}>
          <span style={{ fontSize: 12, color: '#1565C0', flex: 1 }}>Des modifications ont été appliquées au classeur.</span>
          <button
            type="button"
            onClick={() => void handleUndo()}
            disabled={isUndoing}
            style={{
              padding: '5px 12px',
              backgroundColor: '#ff9800',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: isUndoing ? 'not-allowed' : 'pointer',
              fontSize: 11,
              fontWeight: 600,
              opacity: isUndoing ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {isUndoing ? 'Annulation...' : 'Annuler'}
          </button>
        </div>
      )}

      {!scanResult ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p style={{ marginBottom: '16px', color: '#666' }}>
            Lancez l'analyse pour obtenir un diagnostic complet: qualité des données, risques, performance et recommandations.
          </p>
          <button
            onClick={analyzeWorkbook}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? 'Analyse en cours...' : 'Analyser le Classeur'}
          </button>
          {isLoading && <LoadingSpinner label="Analyse complète du classeur..." />}
        </div>
      ) : (
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {/* Summary */}
          {understanding && (
            <div style={{ padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px', marginBottom: '12px' }}>
              <h4 style={sectionTitleStyle}>Résumé</h4>
              <p style={{ margin: '0', fontSize: '13px', lineHeight: '1.5' }}>
                {understanding.summary}
              </p>
            </div>
          )}

          {/* Domain + Complexity */}
          {understanding && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <div style={{ ...cardStyle, flex: 1, marginBottom: 0 }}>
                <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#666' }}>Domaine</p>
                <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold' }}>
                  {understanding.domain.charAt(0).toUpperCase() + understanding.domain.slice(1)}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#999' }}>
                  Confiance: {(understanding.domainConfidence * 100).toFixed(0)}%
                </p>
              </div>
              <div style={{ ...cardStyle, flex: 1, marginBottom: 0 }}>
                <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#666' }}>Complexité</p>
                <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold' }}>
                  {understanding.estimatedComplexity.toUpperCase()}
                </p>
              </div>
            </div>
          )}

          {/* Statistics grid */}
          {scanResult && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              {[
                { label: 'Feuilles', value: scanResult.totals.sheetCount },
                { label: 'Lignes', value: scanResult.totals.totalRows },
                { label: 'Formules', value: scanResult.totals.formulaCount },
                { label: 'Tableaux', value: scanResult.totals.tableCount },
              ].map((stat, i) => (
                <div key={i} style={{ padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', textAlign: 'center' }}>
                  <p style={{ margin: '0 0 2px', fontSize: '10px', color: '#666' }}>{stat.label}</p>
                  <p style={{ margin: '0', fontSize: '16px', fontWeight: 'bold' }}>{stat.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Data Quality Dashboard */}
          {qualityAgg && (
            <div style={{ ...cardStyle, backgroundColor: '#f9fbe7' }}>
              <h4 style={sectionTitleStyle}>Qualité des Données</h4>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  backgroundColor: qualityColor(qualityAgg.quality),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 'bold', fontSize: 16, marginRight: 12,
                }}>
                  {qualityAgg.quality.toFixed(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <QualityBar label="Complétude" value={qualityAgg.completeness} />
                  <QualityBar label="Cohérence" value={qualityAgg.consistency} />
                  <QualityBar label="Unicité" value={qualityAgg.uniqueness} />
                  <QualityBar label="Validité" value={qualityAgg.validity} />
                </div>
              </div>
            </div>
          )}

          {/* Risk & Performance side by side */}
          {(riskReport || performanceReport) && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {riskReport && (
                <div style={{ ...cardStyle, flex: 1, marginBottom: 0, backgroundColor: riskReport.riskScore > 50 ? '#ffebee' : riskReport.riskScore > 20 ? '#fff3e0' : '#e8f5e9' }}>
                  <h4 style={{ ...sectionTitleStyle, fontSize: 13 }}>Risques</h4>
                  <p style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 'bold', color: riskReport.riskScore > 50 ? '#d32f2f' : riskReport.riskScore > 20 ? '#e65100' : '#2e7d32' }}>
                    {riskReport.riskScore}/100
                  </p>
                  <p style={{ ...smallTextStyle, fontSize: 10 }}>
                    {riskReport.totalRisks} risque(s)
                    {riskReport.criticalCount > 0 && <span style={{ color: '#d32f2f' }}> | {riskReport.criticalCount} critique(s)</span>}
                    {riskReport.highCount > 0 && <span style={{ color: '#f44336' }}> | {riskReport.highCount} haut(s)</span>}
                  </p>
                </div>
              )}
              {performanceReport && (
                <div style={{ ...cardStyle, flex: 1, marginBottom: 0, backgroundColor: performanceReport.score >= 80 ? '#e8f5e9' : performanceReport.score >= 50 ? '#fff3e0' : '#ffebee' }}>
                  <h4 style={{ ...sectionTitleStyle, fontSize: 13 }}>Performance</h4>
                  <p style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 'bold', color: qualityColor(performanceReport.score) }}>
                    {performanceReport.score}/100
                  </p>
                  <p style={{ ...smallTextStyle, fontSize: 10 }}>
                    {ratingLabel(performanceReport.overallRating)}
                    {performanceReport.bottlenecks.length > 0 && ` | ${performanceReport.bottlenecks.length} goulot(s)`}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Risk details */}
          {riskReport && riskReport.risks.length > 0 && (
            <div style={{ ...cardStyle, backgroundColor: '#fff8e1' }}>
              <h4 style={sectionTitleStyle}>Détail des Risques ({riskReport.risks.length})</h4>
              {riskReport.risks.slice(0, 8).map((risk, idx) => (
                <div key={idx} style={{ padding: '6px 0', borderBottom: idx < Math.min(riskReport.risks.length, 8) - 1 ? '1px solid #f0e0b0' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={badgeStyle(severityColor(risk.severity))}>{risk.severity}</span>
                    <span style={{ fontSize: 11, fontWeight: 'bold' }}>{risk.category}</span>
                    {risk.sheetName && <span style={{ fontSize: 10, color: '#999' }}>({risk.sheetName})</span>}
                  </div>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: '#555' }}>{risk.description}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 10, color: '#888', fontStyle: 'italic' }}>{risk.recommendation}</p>
                </div>
              ))}
              {riskReport.risks.length > 8 && (
                <p style={{ ...smallTextStyle, fontStyle: 'italic', marginTop: 4 }}>
                  ... et {riskReport.risks.length - 8} autre(s) risque(s)
                </p>
              )}
            </div>
          )}

          {/* Performance issues */}
          {performanceReport && performanceReport.issues.length > 0 && (
            <div style={cardStyle}>
              <h4 style={sectionTitleStyle}>Problèmes de Performance ({performanceReport.issues.length})</h4>
              {performanceReport.issues.slice(0, 6).map((issue, idx) => (
                <div key={idx} style={{ padding: '4px 0', borderBottom: '1px solid #eee' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={badgeStyle(issue.impact === 'high' ? '#f44336' : issue.impact === 'medium' ? '#ff9800' : '#2196F3')}>{issue.impact}</span>
                    <span style={{ fontSize: 11 }}>{issue.description}</span>
                  </div>
                  <p style={{ margin: '2px 0 0', fontSize: 10, color: '#888' }}>{issue.recommendation}</p>
                </div>
              ))}
            </div>
          )}

          {/* Optimization Plan */}
          {optimizationPlan && (optimizationPlan.quickWins.length > 0 || optimizationPlan.priorities.length > 0) && (
            <div style={{ ...cardStyle, backgroundColor: '#e8f5e9' }}>
              <h4 style={sectionTitleStyle}>Plan d'Optimisation</h4>
              {optimizationPlan.quickWins.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 'bold', color: '#2e7d32' }}>Gains rapides:</p>
                  {optimizationPlan.quickWins.map((win, i) => (
                    <p key={i} style={{ margin: '2px 0', fontSize: 11, color: '#555' }}>
                      - {win}
                    </p>
                  ))}
                </div>
              )}
              {optimizationPlan.priorities.length > 0 && (
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 'bold', color: '#555' }}>Priorités ({optimizationPlan.priorities.length}):</p>
                  {optimizationPlan.priorities.slice(0, 5).map((p, i) => (
                    <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #c8e6c9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={badgeStyle(p.impact === 'high' ? '#f44336' : p.impact === 'medium' ? '#ff9800' : '#4caf50')}>
                          {p.impact}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 'bold' }}>{p.title}</span>
                      </div>
                      <p style={{ margin: '2px 0 0', fontSize: 10, color: '#666' }}>{p.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sheet Details (expandable) */}
          {scanResult && scanResult.sheets.length > 0 && (
            <div style={cardStyle}>
              <h4 style={sectionTitleStyle}>Détail par Feuille ({scanResult.sheets.length})</h4>
              {scanResult.sheets.map((sheet, idx) => (
                <SheetDetailRow key={idx} sheet={sheet} />
              ))}
            </div>
          )}

          {/* Workflow */}
          {understanding && understanding.workflowDescription.length > 0 && (
            <div style={cardStyle}>
              <h4 style={sectionTitleStyle}>Flux de Travail</h4>
              {understanding.workflowDescription.map((step, idx) => (
                <p key={idx} style={{ margin: '4px 0', fontSize: '12px', color: '#666' }}>
                  - {step}
                </p>
              ))}
            </div>
          )}

          {/* Critical Areas */}
          {understanding && understanding.criticalAreas.length > 0 && (() => {
            const pendingAreas = understanding.criticalAreas.filter((a) => !appliedFixes.has(a));
            const fixedCount = understanding.criticalAreas.length - pendingAreas.length;
            return (
              <div style={{ padding: '12px', backgroundColor: '#fff3e0', borderRadius: '4px', marginBottom: '12px' }}>
                <h4 style={{ ...sectionTitleStyle, color: '#e65100' }}>
                  Zones Critiques ({pendingAreas.length})
                  {fixedCount > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 'normal', color: '#2e7d32', marginLeft: 8 }}>
                      {fixedCount} corrigée(s)
                    </span>
                  )}
                </h4>

                {pendingAreas.length === 0 && (
                  <div style={{ padding: 10, textAlign: 'center' }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, color: '#2e7d32', fontWeight: 'bold' }}>
                      Toutes les zones critiques ont été traitées.
                    </p>
                    <button
                      type="button"
                      onClick={() => setAppliedFixes(new Set())}
                      style={{ padding: '4px 10px', backgroundColor: 'transparent', color: '#e65100', border: '1px solid #e65100', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}
                    >
                      Réafficher tout
                    </button>
                  </div>
                )}

                {pendingAreas.map((area, idx) => {
                  const mapped = mapCriticalAreaToPriority(area);
                  return (
                    <div
                      key={idx}
                      style={{ border: '1px solid #ffe0b2', borderRadius: 4, padding: 10, marginBottom: 8, background: '#fff' }}
                    >
                      <div style={{ fontSize: 12, color: '#e65100', whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>
                        {area}
                      </div>
                      {mapped && (
                        <div style={{ marginTop: 8 }}>
                          <button
                            type="button"
                            onClick={() => void applyFixForCriticalArea(area)}
                            disabled={isApplyingFix}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#2196F3',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: isApplyingFix ? 'not-allowed' : 'pointer',
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {isApplyingFix ? 'Application...' : mapped.label}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {fixedCount > 0 && pendingAreas.length > 0 && (
                  <div style={{ textAlign: 'right', marginTop: 4 }}>
                    <button
                      type="button"
                      onClick={() => setAppliedFixes(new Set())}
                      style={{ padding: '2px 8px', backgroundColor: 'transparent', color: '#999', border: 'none', cursor: 'pointer', fontSize: 10, textDecoration: 'underline' }}
                    >
                      Réafficher les {fixedCount} zone(s) corrigée(s)
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Action Buttons */}
          {scanResult && understanding && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button
                onClick={handleCreateSummarySheet}
                style={{
                  flex: 1, padding: '8px 12px', backgroundColor: '#2196F3',
                  color: 'white', border: 'none', borderRadius: '4px',
                  cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
                }}
              >
                Créer Résumé
              </button>
              <button
                onClick={handleCreateRecommendationsSheet}
                style={{
                  flex: 1, padding: '8px 12px', backgroundColor: '#ff9800',
                  color: 'white', border: 'none', borderRadius: '4px',
                  cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
                }}
              >
                Recommandations
              </button>
            </div>
          )}

          {/* Re-analyze Button */}
          <button
            onClick={analyzeWorkbook}
            disabled={isLoading}
            style={{
              width: '100%', padding: '10px', backgroundColor: '#2196F3',
              color: 'white', border: 'none', borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? 'Analyse en cours...' : 'Réanalyser'}
          </button>
        </div>
      )}
    </div>
  );
};
